import {
  ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation, Inject, Output, EventEmitter,
  ChangeDetectorRef, OnDestroy, Input, SimpleChanges, OnChanges
} from '@angular/core';
import { format } from 'date-fns';
import { Router } from '@angular/router';
import { Subscription, filter, tap, take, switchMap, map } from 'rxjs';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { isNullOrUndefined } from '@app/util/util';
import { environment as ENV } from '@environment';

import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { ImageCdnPipe } from '@app/pipes/image-cdn.pipe';
import { SearchType } from '@search-bar/entities/search-type';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { HotelsSearchAbstractComponent } from '@hotels/hotel-search/abstracts/hotels-search.abstract';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { byhoursResultsRootRoute, hotelByhoursRootRoute } from '@hotels/hotel-results/hotel-results-routing.module';
import { SessionStorageService } from '@app/services/session-storage.service';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { hotelDetailRootRoute } from '@hotels/hotel-detail/hotel-detail-routing.module';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { AVO_EVENTS } from '@app/modules/avo/events/avo.events';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { SALES_CHANNEL } from '@app/modules/core/constants/query-params';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { HotelDestinationInputType } from '@shared/base-search-bar/entities/autocomplete-destionation';

@Component({
  selector: 'app-byhours-search',
  templateUrl: './byhours-search.component.html',
  styleUrls: ['./byhours-search.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ByhoursSearchComponent extends HotelsSearchAbstractComponent implements OnInit, OnDestroy, OnChanges {

  public arrivalDate: Date;
  public departureDate: Date;
  public showCalendarInput = false;

  public activeCalendarType: CalendarField = CalendarField.departure;
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public isSubmited = false;
  public readonly subscriptions: Subscription[] = [];
  public hourType: string[];
  
  protected AvoDispatch = new AvoDispatchOrchestrator();

  @Input() source: EVENT_SOURCE | string;
  @Input() showSearch = false;
  @Input() loadSearchData = false;
  @Input() isResults = false;
  @Input() isMobile: boolean;
  @Input() loadDefaultplaces: boolean;
  @Input() hotelDetail: HotelDetailResponse;
  @Input() showHotelDetail: boolean;

  @Output() showDestination: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() redirectToSearch: EventEmitter<void> = new EventEmitter<void>();
  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();

  public readonly icons = Icon;

  constructor(
    protected amplitude: PropertiesAmplitudeService,
    readonly baseSearchbarfacade: BaseSearchBarFacade,
    readonly cdr: ChangeDetectorRef,
    readonly avoService: AvoService,
    readonly imageCdnPipe: ImageCdnPipe,
    readonly sessionStorageService: SessionStorageService,
    readonly router: Router,
    private triggerAmplitudeService: TriggerAmplitudeService,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(baseSearchbarfacade, avoService, amplitude);
    this.loadDefaultplaces = true;
    this.searchType = SearchType.HOME_BYHOURS;
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      if (!this.isResults && !this.hotelDetail && this.loadDefaultplaces) {
        this.baseSearchbarfacade.loadSearchData(SearchBy.HOTELS);
      }
      this.subscriptions.push(
        this.baseSearchbarfacade.searchData$
          .pipe(
            filter(data => !!data),
            tap((searchData) => {
              this.placeSelectedItem = searchData.hotelDestinationPlace;
              this.departureDate = searchData.departureDate;
              this.hourType = searchData.hourType;
              this.cdr.detectChanges();
              this.cdr.markForCheck();
            })
          ).subscribe(),
      );
      if (this.searchType === SearchType.HOME_BYHOURS) {
        this.registerViewOrSelectAmplitudeEvent(this.source);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showSearch && changes.showSearch.currentValue) {
      const source = (this.source === EVENT_SOURCE.RESULTS && this.showHotelDetail ? EVENT_SOURCE.DETAIL : this.source);
      this.registerViewOrSelectAmplitudeEvent(source);
    }
  }

  ngOnDestroy(): void {
    this.cdr.detach();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isByHoursDetail(): boolean {
    return this.searchType === SearchType.BYHOURS_DETAIL;
  }

  get searchValid(): boolean {
    const validatePlace = this.isByHoursDetail ? true : !this.isPlaceSelectedEmpty;
    return validatePlace && !isNullOrUndefined(this.departureDate);
  }

  get showMessageInfo(): boolean {
    return this.source === EVENT_SOURCE.TRAVEL_HOME;
  }

  public navigateToSearch(): void {
    this.isSubmited = true;
    if (this.searchValid) {
      this.baseSearchbarfacade.searchData$.pipe(
        take(1),
        tap(data => {
          this.isSubmited = false;
          this.baseSearchbarfacade.saveSearchData(data, SearchBy.BYHOURS);
          this.search();
          if (this.showHotelDetail) {
            this.redirectToSearch.emit();
          }
        })
      ).subscribe().unsubscribe();
    }
  }

  private search(): void {
    const _departureDate = format(this.departureDate, 'yyyy-MM-dd');
    if (!this.isByHoursDetail) {
      this.baseSearchbarfacade.searchData$.pipe(
        take(1),
        tap(() => {
          this.redirectToSearch.emit();
          this.sessionStorageService.setEncodeDestinationType(this.placeSelectedItem?.InputType);
          this.sessionStorageService.setEncodeDestinationId(this.placeSelectedItem?.Id);
          this.sessionStorageService.setEncodeDestinationIATA(this.placeSelectedItem?.NearestIata);
        }),
        switchMap(data => this.amplitude.searchProperties$(SearchBy.BYHOURS).pipe(
          take(1), map(searchProps => ({
            _source: this.showHotelDetail ? EVENT_SOURCE.DETAIL : this.source, searchProps
          })),
          switchMap(({ searchProps, _source }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
            AVO_EVENTS.SELECT_TRAVEL_SEARCH, { SOURCE: _source }, searchProps
          ).pipe(
            tap(trigger => {
              triggerAmplitudeEventApp(trigger);
            })
          )),
          tap(() => {
            this.router.navigate([this.getUrlRedirectByResults(_departureDate)],
              { queryParams: { source: this.source, branch_code: data.branchCode, [SALES_CHANNEL]: data.salesChannel } });
          })
        )),
      ).subscribe().unsubscribe();
    }else if (this.hotelDetail && this.hotelDetail.Result) {
      this.registerViewOrSelectAmplitudeEvent(this.source, true);
      this.router.navigate([
        `/${hotelDetailRootRoute}/${this.hotelDetail.Result.HotelId}/${this.hotelDetail.Result.Destination}` +
        `/${_departureDate}/2_0/${ENV.countryInfo.currency_code}`
      ], { queryParams: { source: isNullOrUndefined(this.source) ? EVENT_SOURCE.DETAIL : this.source } });
    }
  }

  private getUrlRedirectByResults(_departureDate: string ): string {
    if (this.placeSelectedItem?.isPopularSelection) {
      return `${hotelByhoursRootRoute}/${byhoursResultsRootRoute}/${HotelDestinationInputType.SearchBar}` +
        `/${this.placeSelectedItem?.NearestIata}/${_departureDate}/${ENV.countryInfo.currency_code}/2_0/${this.hourType?.[0]}`;
    } else {
      return `${hotelByhoursRootRoute}/${byhoursResultsRootRoute}/${this.placeSelectedItem?.InputType}` +
      `/${this.placeSelectedItem?.Id}/${_departureDate}/${ENV.countryInfo.currency_code}/2_0/${this.hourType?.[0]}`;
    }
  }

}
